import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './ActsisDashboard.css';

function ActsisDashboard() {
    const { sistemaId } = useParams();
    const { token, user: currentUser } = useAuth();
    
    // Estados para los datos del dashboard
    const [stats, setStats] = useState({ completas: 0, pendientes: 0, vencidas: 0, completas_fuera_de_tiempo: 0 });
    const [trabajosStats, setTrabajosStats] = useState([]);
    
    // Estados para la UI y filtros
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activePeriod, setActivePeriod] = useState('semana');
    const [filtroUsuario, setFiltroUsuario] = useState('todos');
    const [filtroMarca, setFiltroMarca] = useState('todas');
    const [filtroAgencia, setFiltroAgencia] = useState('todas');
    
    // Estados para las listas de los filtros
    const [usuariosDelSistema, setUsuariosDelSistema] = useState([]);
    const [listaAgencias, setListaAgencias] = useState([]);

    // Carga los datos para los menús desplegables (filtros)
    useEffect(() => {
        const fetchFilterData = async () => {
            if (!token) return;
            try {
                const headers = { 'Authorization': `Bearer ${token}` };
                const [usuariosRes, agenciasRes] = await Promise.all([
                    currentUser.rol === 'admin' ? fetch(`/api/sistemas/${sistemaId}/usuarios`, { headers }) : Promise.resolve({ ok: true, json: () => Promise.resolve([]) }),
                    fetch('/api/agencias-accesibles', { headers })
                ]);

                if (!usuariosRes.ok || !agenciasRes.ok) throw new Error('No se pudo cargar la información para los filtros.');
                
                const usuariosData = await usuariosRes.json();
                const agenciasData = await agenciasRes.json();
                
                setUsuariosDelSistema(usuariosData);
                setListaAgencias(agenciasData);
            } catch (err) {
                setError(err.message);
            }
        };
        fetchFilterData();
    }, [token, sistemaId, currentUser.rol]);

    // Carga las estadísticas principales del dashboard
    const fetchStats = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                periodo: activePeriod,
                usuarioId: filtroUsuario,
                marca: filtroMarca,
                agenciaId: filtroAgencia
            });
            const response = await fetch(`/api/actsis/${sistemaId}/stats?${params.toString()}`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                    cache: 'no-store' 
                });
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.message || 'No se pudieron cargar las estadísticas.');
            }
            const data = await response.json();
            setStats(data.stats);
            setTrabajosStats(data.trabajosStats);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [token, sistemaId, activePeriod, filtroUsuario, filtroMarca, filtroAgencia]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    // --- NUEVA FUNCIÓN ---
    const handleLimpiarFiltros = () => {
        setActivePeriod('semana');
        setFiltroUsuario('todos');
        setFiltroMarca('todas');
        setFiltroAgencia('todas');
    };

    const renderStatCard = (title, value, className = 'text-dark') => (
        <div className="col-md-6 col-lg-3 mb-4">
            <div className="stat-card h-100">
                <h5 className="stat-title">{title}</h5>
                <p className={`stat-value ${className}`}>{loading ? '...' : value}</p>
            </div>
        </div>
    );
    
    const marcasDisponibles = [...new Set(listaAgencias.map(ag => ag.marca))];
    const agenciasFiltradasPorMarca = filtroMarca === 'todas' ? listaAgencias : listaAgencias.filter(ag => ag.marca === filtroMarca);

    return (
        <div className="container-fluid mt-4 px-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="dashboard-title">Dashboard de Actividades</h2>
                <Link to={`/sistemas/${sistemaId}`} className="btn btn-outline-secondary">
                    ← Volver a la Lista de Tareas
                </Link>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <div className="d-flex flex-wrap justify-content-between align-items-center time-filters">
                        <div className="btn-group mb-2 mb-md-0" role="group">
                            <button className={`btn btn-filter ${activePeriod === 'dia' ? 'active' : ''}`} onClick={() => setActivePeriod('dia')}>Hoy</button>
                            <button className={`btn btn-filter ${activePeriod === 'semana' ? 'active' : ''}`} onClick={() => setActivePeriod('semana')}>Esta Semana</button>
                            <button className={`btn btn-filter ${activePeriod === 'mes' ? 'active' : ''}`} onClick={() => setActivePeriod('mes')}>Este Mes</button>
                        </div>
                        
                        <div className="d-flex flex-wrap gap-2 align-items-center">
                             <select className="form-select form-select-sm" value={filtroMarca} onChange={e => { setFiltroMarca(e.target.value); setFiltroAgencia('todas'); }} style={{ width: 'auto' }}>
                                <option value="todas">Todas las Marcas</option>
                                {marcasDisponibles.map(marca => <option key={marca} value={marca}>{marca}</option>)}
                            </select>
                             <select className="form-select form-select-sm" value={filtroAgencia} onChange={e => setFiltroAgencia(e.target.value)} style={{ width: 'auto' }}>
                                <option value="todas">Todas las Agencias</option>
                                {agenciasFiltradasPorMarca.map(ag => <option key={ag.id} value={ag.id}>{ag.nombre}</option>)}
                            </select>
                            {currentUser.rol === 'admin' && (
                                <select className="form-select form-select-sm" value={filtroUsuario} onChange={(e) => setFiltroUsuario(e.target.value)} style={{width: 'auto'}}>
                                    <option value="todos">Todos los Usuarios</option>
                                    {usuariosDelSistema.map(user => (
                                        <option key={user.id} value={user.id}>{user.nombre}</option>
                                    ))}
                                </select>
                            )}
                            <button className="btn btn-sm btn-outline-primary" onClick={handleLimpiarFiltros}>
                                Limpiar Filtros
                            </button>
                        </div>
                    </div>
                </div>
            </div>

           <div className="row mt-3">
                {renderStatCard('Actividades Completas', stats.completas, 'text-success')}
                {renderStatCard('Completas Fuera de Tiempo', stats.completas_fuera_de_tiempo, 'text-primary')}
                {renderStatCard('Actividades Pendientes', stats.pendientes, 'text-warning')}
                {renderStatCard('Actividades Vencidas', stats.vencidas, 'text-danger')}
            </div>

            <div className="card shadow-sm mt-4">
                <div className="card-header">
                    <h5 className="mb-0">Tareas por Tipo de Trabajo</h5>
                </div>
                <div className="card-body">
                    {loading ? (
                        <div className="d-flex justify-content-center p-3">
                            <div className="spinner-border" role="status"><span className="visually-hidden">Cargando...</span></div>
                        </div>
                    ) : trabajosStats.length > 0 ? (
                        <ul className="list-group list-group-flush">
                            {trabajosStats.map(trabajo => (
                                <li key={trabajo.trabajo_nombre} className="list-group-item d-flex justify-content-between align-items-center">
                                    {trabajo.trabajo_nombre}
                                    <span className="badge bg-primary rounded-pill">{trabajo.cantidad}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-center text-muted">No hay datos de trabajos para el período o filtros seleccionados.</p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ActsisDashboard;